import { useNavigate } from "react-router-dom";
import { Radio, ShieldCheck, Ticket, ArrowRight, CheckCircle2 } from "lucide-react";
import BypassTicketIcon from "../components/BypassTicketIcon";
import BypassEstablishmentIcon from "../components/BypassEstablishmentIcon";
import CylinderPhrases from "../components/CylinderPhrases";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";

export default function Home() {
  const nav = useNavigate();
  const { role, commerce } = useAuth();
  // The home page is intentionally the SAME public landing for everyone
  // (anonymous, guest visitor, signed-in merchant) — when a merchant taps
  // the "Accueil" shortcut they expect to land here, with the two role
  // cards visible exactly like a fresh visit. Returning to the dashboard
  // is handled by the persistent "Tableau de bord" button in the header.
  const isAnonymousLanding = true;
  const showGreetingBanner = false;
  // An establishment in the middle of email verification must NOT see the
  // "Tableau de bord" shortcut from the home landing — they can't create a
  // new establishment (a verification is pending), but they CAN still log
  // in to a different already-verified establishment they may own. So we
  // route the Établissement card to /commerce/login in that state, with
  // the original wording "Gérer ma file d'attente" everywhere.
  const merchantUnverified = role === "commerce" && commerce?.emailVerified === false;
  const merchantLoggedIn = role === "commerce" && !merchantUnverified;

  return (
    <div>
      {showGreetingBanner && (
        <section className="border-b border-border bg-surface/60" data-testid="greeting-banner">
          <div className="container mx-auto px-4 md:px-8 py-6 md:py-8">
            <div>
              <h1 className="font-display text-2xl md:text-4xl font-black tracking-tight text-ink" data-testid="greeting-title">
                Bonjour 👋
              </h1>
              <p className="mt-2 text-subtle text-sm md:text-base">
                {commerce?.name
                  ? <>Vous êtes connecté à <strong className="text-ink">{commerce.name}</strong></>
                  : "Vous êtes connecté à votre établissement."}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* INTRO + CHOICES — fits in a single viewport on mobile & desktop */}
      <section className="relative border-b border-border">
        <div className="container relative mx-auto px-4 md:px-8 pt-2 pb-2 md:pt-8 md:pb-10">
          {/* Big logo on the home page (only when visitor not authenticated) */}
          {isAnonymousLanding && (
            <div className="flex flex-col items-center md:items-start mb-1 md:mb-4" data-testid="home-logo-block">
              <img
                src="/logo-bypassgo-b-only.png"
                alt="BypassGo"
                translate="no"
                className="object-contain h-16 md:h-28 w-auto"
              />
              <p className="mt-1 md:mt-2 text-[10px] md:text-sm tracking-[0.18em] uppercase text-subtle font-medium">
                La file d'attente intelligente
              </p>
            </div>
          )}

          {/* "Bienvenue sur BypassGo." — centered, compact */}
          {isAnonymousLanding && (
            <p
              className="text-center font-display text-lg md:text-2xl font-black tracking-tight text-ink"
              data-testid="home-welcome-title"
            >
              Bienvenue sur <span translate="no">BypassGo</span>.
            </p>
          )}

          {/* Rotating catchphrases — only for unauthenticated visitors */}
          {isAnonymousLanding && (
            <div className="mt-2 md:mt-4" data-testid="home-rotating-phrases">
              <CylinderPhrases />
            </div>
          )}

          {/* Entry cards — role-adaptive. Side-by-side from the smallest
             screens up so they fit above the fold on mobile. */}
          <div className={`mt-3 md:mt-6 grid ${isAnonymousLanding ? "grid-cols-2" : "grid-cols-1 max-w-2xl mx-auto"} gap-2 md:gap-6 items-start`}>
            {(isAnonymousLanding || role === "client") && (
              <div className="flex flex-col gap-2">
                <EntryBlock
                  tag="Visiteur"
                  badge="100 % Gratuit"
                  title="Accéder à une file d'attente"
                  desc="Trouvez un établissement et prenez un ticket."
                  buttonLabel="Chercher ma file d'attente"
                  onClick={() => nav("/search")}
                  accent="brand"
                  icon={<BypassTicketIcon size={28} />}
                  testid="entry-user"
                />
                {/* Placeholder invisible reproduisant la place occupée par
                   le lien « Déjà inscrit ? Se connecter » sous la carte
                   Établissement, pour que les deux cartes (Visiteur et
                   Établissement) soient strictement symétriques en hauteur. */}
                {!merchantLoggedIn && !merchantUnverified && (
                  <p
                    className="invisible text-center text-sm md:text-base select-none"
                    aria-hidden="true"
                    data-testid="entry-user-symmetry-spacer"
                  >
                    Déjà inscrit&nbsp;? Se connecter
                  </p>
                )}
              </div>
            )}
            {(isAnonymousLanding || role === "commerce") && (
              <div className="flex flex-col gap-2">
                <EntryBlock
                  tag="Établissement"
                  /* Spacer invisible reproduisant la hauteur du badge
                     « 100 % Gratuit » de la carte Visiteur pour garder
                     les deux cartes strictement symétriques en hauteur. */
                  badgeSpacer
                  title={merchantLoggedIn ? "Gérer ma file d'attente" : "Créer une file d'attente"}
                  desc={merchantLoggedIn
                    ? "Gérez votre file en temps réel depuis votre tableau de bord."
                    : merchantUnverified
                      ? "Connectez-vous à un autre établissement déjà actif (une vérification est en cours sur le nouveau compte)."
                      : "Gérez la file d'attente de votre établissement."}
                  buttonLabel={merchantLoggedIn
                    ? "Tableau de bord"
                    : merchantUnverified
                      ? "Gérer ma file d'attente"
                      : "Créer ma file d'attente"}
                  onClick={() => nav(merchantLoggedIn
                    ? "/merchant/dashboard"
                    : merchantUnverified
                      ? "/commerce/login"
                      : "/commerce/register")}
                  accent="ink"
                  icon={<BypassEstablishmentIcon size={28} />}
                  testid="entry-structure"
                />
                {/* Shortcut « déjà inscrit » → masqué quand l'utilisateur
                   est déjà connecté merchant (raccourci redondant). */}
                {!merchantLoggedIn && !merchantUnverified && (
                  <p className="text-center text-sm md:text-base text-ink" data-testid="entry-structure-login-shortcut-wrap">
                    Déjà inscrit&nbsp;?{" "}
                    <button
                      type="button"
                      onClick={() => nav("/commerce/login")}
                      className="font-bold text-brand underline-offset-2 hover:underline hover:text-brand-hover transition-colors"
                      data-testid="entry-structure-login-shortcut"
                    >
                      Se connecter
                    </button>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* SECONDARY INFO — desktop only, kept below the fold on mobile */}
      <section className="hidden md:block border-b border-border">
        <div className="container mx-auto px-4 md:px-8 py-10 md:py-16">
          <p className="overline mb-4">Plateforme de gestion de files d'attente · temps réel</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl">
            <Feature icon={<BypassTicketIcon size={26} />} title="Ticket à distance" desc="Inscrivez-vous à la file depuis chez vous, sans attendre sur place." />
            <Feature icon={<Radio size={18} />} title="Suivi temps réel" desc="Position en direct et estimation précise de votre tour." />
            <Feature icon={<ShieldCheck size={18} />} title="RGPD · Confidentialité" desc="Écran public anonymisé, données privées protégées." />
          </div>
        </div>
      </section>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="border-l-2 border-ink pl-4">
      <div className="flex items-center gap-2 text-ink font-medium">{icon} {title}</div>
      <p className="mt-1 text-sm text-subtle">{desc}</p>
    </div>
  );
}

function EntryBlock({ tag, badge, badgeSpacer, title, desc, buttonLabel, onClick, accent, icon, testid }) {
  const isBrand = accent === "brand";
  return (
    <div
      className={`group h-full border-2 ${isBrand ? "border-brand" : "border-ink"} p-3 md:p-6 flex flex-col transition-colors hover:bg-surface`}
      data-testid={testid}
    >
      {/* Icon on its own line (top-right) so the tag below has the full
         card width — required for "ÉTABLISSEMENT" to render fully on
         narrow mobile cards. */}
      <div className={`flex justify-end ${isBrand ? "text-brand" : "text-ink"}`}>
        {icon}
      </div>
      <span
        className={`overline ${isBrand ? "text-brand" : "text-ink"} text-[10px] md:text-xs mt-1 md:mt-2`}
      >
        {tag}
      </span>
      {/* « 100 % Gratuit » pill (spec utilisateur fork 25.4) — uniquement
         pour la carte Visiteur. Sur la carte Établissement on rend un
         placeholder strictement invisible de même hauteur pour conserver
         la symétrie verticale parfaite des deux cartes. */}
      {badge && (
        <span
          className="self-start inline-flex items-center gap-1 mt-1.5 md:mt-2 px-2 py-0.5 rounded-full bg-[#0A7F2E]/10 text-[#0A7F2E] text-[10px] md:text-xs font-bold leading-none"
          data-testid={`${testid}-badge`}
        >
          <CheckCircle2 size={12} className="shrink-0" aria-hidden="true" />
          {badge}
        </span>
      )}
      {badgeSpacer && (
        <span
          className="invisible self-start inline-flex items-center gap-1 mt-1.5 md:mt-2 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-bold leading-none select-none"
          aria-hidden="true"
          data-testid={`${testid}-badge-spacer`}
        >
          <CheckCircle2 size={12} className="shrink-0" />
          100 % Gratuit
        </span>
      )}
      <h3 className="mt-2 md:mt-3 font-display text-sm md:text-2xl font-black tracking-tighter text-ink leading-tight min-h-[2.4rem] md:min-h-[3.4rem]">
        {title}
      </h3>
      <p className="mt-1 md:mt-2 text-[11px] md:text-sm text-subtle leading-snug line-clamp-2 md:line-clamp-none">{desc}</p>
      {/* mt-auto pushes the button to the bottom so both cards have the
         same visual rhythm even if titles/descriptions differ in length. */}
      <Button
        onClick={onClick}
        className={`mt-auto pt-2 md:pt-3 min-h-9 md:min-h-11 h-auto py-2 rounded-md font-medium self-stretch md:self-start px-3 md:px-5 text-xs md:text-base leading-tight ${isBrand ? "bg-brand hover:bg-brand-hover text-white" : "bg-ink hover:bg-brand text-white"}`}
        data-testid={`${testid}-btn`}
      >
        <span className="whitespace-normal text-center">{buttonLabel}</span>
        <ArrowRight size={14} className="ml-1.5 shrink-0" />
      </Button>
    </div>
  );
}
